/**
 * Token & Device Wizard — payment → PAYG days only:
 * record cash → approve → token, or mint from a completed payment.
 * Counter sync / set count / full reset live under OpenPAYGO Exceptions (dual control).
 *
 * Modal + MUI Stepper. Parent keeps state across steps.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { AppBadge, AppButton, AppField, AppInput, AppSelect } from "../../../shared/components/ui";
import { IconCheck, IconSpinner, IconWarning } from "../../../shared/components/icons";
import { ApiError, api } from "../../../shared/api/client";
import { DaysEstimateHint } from "../../payments/components/DaysEstimateHint";

export type WizardIntent = "release_days";

/** How we will obtain a payment for day credit (device-detail path). */
export type PaymentSource = "record_cash" | "approve_pending" | "mint_completed";

export type WizardPayment = {
  id: number;
  payment_reference: string;
  amount: number;
  method: string;
  status: string;
  customer_id: number;
  customer_name: string | null;
  device_serial: string | null;
  created_at?: string | null;
  service_days?: number | null;
  estimated_days?: number | null;
  estimated_days_message?: string | null;
  token_days?: number | null;
  /** Receipt recorded at create time — approve must match this. */
  provider_reference?: string | null;
  transaction_id?: string | null;
  mobile_money_reference?: string | null;
};

export type WizardDevice = {
  id: number;
  serial_number: string;
  token_count: number;
  status: string;
  has_openpaygo_credentials?: boolean;
  customer?: { id: number; name: string; customer_number: string } | null;
};

type TokenResult = {
  token: string;
  token_type?: string;
  new_token_count?: number;
  days_added?: number;
  entry_hint?: string;
  message?: string;
};

const PROVIDERS = [
  { value: "cash", label: "Cash / office" },
  { value: "vodacom", label: "Vodacom M-Pesa" },
  { value: "airtel", label: "Airtel Money" },
  { value: "tigo", label: "Tigo Pesa" },
];

function recordedReceipt(p: WizardPayment | null | undefined): string {
  return (
    p?.provider_reference
    || p?.transaction_id
    || p?.mobile_money_reference
    || ""
  ).trim();
}

export function isCashLike(method: string): boolean {
  const m = method.toLowerCase();
  return method.toLowerCase() === "cash" || m === "office_cash" || m === "agent_collection";
}

export function isMobileLike(method: string): boolean {
  const m = method.toLowerCase();
  return m === "mobile_money" || m === "ussd" || m === "mobile_app" || m.includes("mpesa");
}

function formatTzs(amount: number): string {
  return `TZS ${amount.toLocaleString("en-US")}`;
}

function mergeById(lists: WizardPayment[][]): WizardPayment[] {
  const map = new Map<number, WizardPayment>();
  for (const list of lists) {
    for (const p of list) map.set(p.id, p);
  }
  return Array.from(map.values()).sort((a, b) => b.id - a.id);
}

function friendlyError(ex: unknown): string {
  if (ex instanceof ApiError) {
    const hint = typeof ex.details?.hint === "string" ? ex.details.hint : null;
    if (hint) return hint;
    if (ex.status === 403) {
      return `${ex.message} (need payment.process — Business Admin or Commercial Officer).`;
    }
    if (ex.status === 502 || ex.status === 503) {
      return "Device service unreachable (gateway 502/503). Retry in a minute, or check Coolify device-service health.";
    }
    return ex.message;
  }
  return ex instanceof Error ? ex.message : "Something went wrong";
}

type Props = {
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
  deviceSerial?: string | null;
  payment?: WizardPayment | null;
  initialIntent?: WizardIntent | null;
};

export function TokenDeviceWizard({
  open,
  onClose,
  onDone,
  deviceSerial = null,
  payment = null,
  initialIntent = null,
}: Props) {
  const [step, setStep] = useState(0);
  const [intent, setIntent] = useState<WizardIntent | null>(initialIntent);
  const [device, setDevice] = useState<WizardDevice | null>(null);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [pendingCash, setPendingCash] = useState<WizardPayment[]>([]);
  const [pendingMobile, setPendingMobile] = useState<WizardPayment[]>([]);
  const [completed, setCompleted] = useState<WizardPayment[]>([]);
  const [source, setSource] = useState<PaymentSource | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<WizardPayment | null>(payment);
  const [cashAmount, setCashAmount] = useState("");
  const [reference, setReference] = useState("");
  const [provider, setProvider] = useState("cash");
  const [days, setDays] = useState("7");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<TokenResult | null>(null);

  const labels = useMemo(() => {
    return ["Payment", "Confirm & release", "Done"];
  }, []);

  const reset = useCallback(() => {
    setStep(0);
    setIntent("release_days");
    setDevice(null);
    setPendingCash([]);
    setPendingMobile([]);
    setCompleted([]);
    setSource(payment ? (payment.status === "completed" ? "mint_completed" : "approve_pending") : null);
    setSelectedPayment(payment);
    setCashAmount("");
    setReference(recordedReceipt(payment));
    setProvider("cash");
    const prefill =
      payment?.service_days ??
      payment?.estimated_days ??
      payment?.token_days ??
      7;
    setDays(String(prefill));
    setBusy(false);
    setError("");
    setResult(null);
  }, [initialIntent, payment]);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  useEffect(() => {
    if (!open) return;
    const serial = deviceSerial || selectedPayment?.device_serial;
    if (!serial) return;
    let cancelled = false;
    setDeviceLoading(true);
    api
      .get<{ device: WizardDevice }>(`/devices/${serial}`)
      .then((d) => {
        if (!cancelled) setDevice(d.device);
      })
      .catch((ex) => {
        if (!cancelled) setError(friendlyError(ex));
      })
      .finally(() => {
        if (!cancelled) setDeviceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, deviceSerial, selectedPayment?.device_serial]);

  const loadPayments = useCallback(async (dev: WizardDevice) => {
    setPaymentsLoading(true);
    try {
      const customerId = dev.customer?.id;
      const fetches: Promise<{ payments: WizardPayment[] }>[] = [
        api.get(`/payments?status=pending&device_id=${dev.id}&per_page=30`),
        api.get(`/payments?status=completed&device_id=${dev.id}&per_page=30`),
      ];
      if (customerId) {
        fetches.push(api.get(`/payments?status=pending&customer_id=${customerId}&per_page=30`));
        fetches.push(api.get(`/payments?status=completed&customer_id=${customerId}&per_page=30`));
      }
      const results = await Promise.all(fetches);
      const pendingMerged = mergeById([
        results[0]?.payments ?? [],
        ...(customerId ? [results[2]?.payments ?? []] : []),
      ]);
      const completedMerged = mergeById([
        results[1]?.payments ?? [],
        ...(customerId ? [results[3]?.payments ?? []] : []),
      ]);
      setPendingCash(pendingMerged.filter((p) => isCashLike(p.method)));
      setPendingMobile(pendingMerged.filter((p) => isMobileLike(p.method)));
      setCompleted(completedMerged);
    } finally {
      setPaymentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !device?.id || intent !== "release_days") return;
    let cancelled = false;
    loadPayments(device).catch((ex) => {
      if (!cancelled) setError(friendlyError(ex));
    });
    return () => {
      cancelled = true;
    };
  }, [open, device, intent, loadPayments]);

  const close = () => {
    onClose();
    onDone?.();
  };

  const approveCashPayment = async (payId: number) => {
    const res = await api.post<TokenResult & { token_value?: string; days?: number; message?: string }>(
      `/payments/${payId}/approve`,
      {
        cash_approval_reference: reference.trim(),
        provider,
      },
    );
    const token = res.token ?? res.token_value;
    if (!token) {
      throw new Error(res.message || "Payment approved but no token was returned. Check device assignment.");
    }
    return {
      token,
      token_type: "add_time" as const,
      days_added: res.days_added ?? res.days,
      entry_hint: `*${token}#`,
      message: res.message ?? "Payment approved — token issued.",
    };
  };

  const goNext = async () => {
    setError("");
    if (step === 0) {
      // Prefill from Payment Approvals
      if (payment) {
        if (payment.status === "pending" && isMobileLike(payment.method)) {
          setError(
            "This is a mobile-money payment still pending provider confirmation. Wait for the webhook — cash collections only here.",
          );
          return;
        }
        setSelectedPayment(payment);
        setSource(payment.status === "completed" ? "mint_completed" : "approve_pending");
        setStep(1);
        return;
      }

      if (!source) {
        setError("Choose how to release days: record cash now, approve pending cash, or mint from a completed payment.");
        return;
      }

      if (source === "record_cash") {
        if (!device?.customer?.id) {
          setError("Assign a customer to this device before recording a cash collection.");
          return;
        }
        setStep(1);
        return;
      }

      if (source === "approve_pending") {
        if (!selectedPayment || selectedPayment.status !== "pending" || !isCashLike(selectedPayment.method)) {
          setError("Select a pending cash payment to approve.");
          return;
        }
        setStep(1);
        return;
      }

      if (source === "mint_completed") {
        if (!selectedPayment || selectedPayment.status === "pending") {
          setError("Select a completed payment to mint days.");
          return;
        }
        setStep(1);
        return;
      }
      return;
    }

    if (step === 1) {
      setBusy(true);
      try {
        if (source === "record_cash") {
          if (!device?.customer?.id) throw new Error("Device has no customer");
          const amount = Number(cashAmount.replace(/,/g, ""));
          if (!Number.isFinite(amount) || amount <= 0) {
            throw new Error("Enter a valid cash amount in TZS.");
          }
          if (reference.trim().length < 6) {
            throw new Error("Enter an approval / receipt reference of at least 6 characters.");
          }
          const created = await api.post<{
            id: number;
            payment_reference: string;
            status: string;
            device_serial?: string;
          }>("/payments", {
            customer_id: device.customer.id,
            device_id: device.id,
            amount,
            method: "cash",
            channel: "CASH",
            provider: "cash",
          });
          const tokenResult = await approveCashPayment(created.id);
          setResult({
            ...tokenResult,
            message: `Cash ${created.payment_reference} recorded and approved — token issued.`,
          });
        } else if (source === "approve_pending" && selectedPayment) {
          if (reference.trim().length < 6) {
            throw new Error("Enter an approval reference of at least 6 characters.");
          }
          setResult(await approveCashPayment(selectedPayment.id));
        } else if (source === "mint_completed" && selectedPayment) {
          const serial = selectedPayment.device_serial || device?.serial_number;
          if (!serial) throw new Error("Payment has no device serial.");
          const res = await api.post<TokenResult>(`/devices/${serial}/openpaygo/token`, {
            token_type: "add_time",
            value: parseInt(days, 10) || 7,
            payment_id: selectedPayment.id,
          });
          setResult({
            token: res.token,
            token_type: res.token_type,
            new_token_count: res.new_token_count,
            entry_hint: res.entry_hint ?? `*${res.token}#`,
            message: "Token generated from completed payment.",
          });
        } else {
          throw new Error("Nothing to run — go back and choose a payment path.");
        }
        setStep(2);
      } catch (ex) {
        setError(friendlyError(ex));
      } finally {
        setBusy(false);
      }
    }
  };

  const goBack = () => {
    setError("");
    if (step > 0 && step < 2) setStep((s) => s - 1);
  };

  const customerLabel = device?.customer
    ? `${device.customer.name} (${device.customer.customer_number})`
    : null;

  return (
    <Dialog open={open} onClose={busy ? undefined : close} fullWidth maxWidth="sm">
      <DialogTitle>Token &amp; device wizard</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3, mt: 1 }}>
          {labels.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error ? (
          <Box
            role="alert"
            sx={{
              mb: 2,
              p: 1.5,
              bgcolor: "error.50",
              color: "error.dark",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "error.light",
              fontSize: "0.875rem",
            }}
          >
            <IconWarning size={14} /> {error}
          </Box>
        ) : null}

        {step === 0 && (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Day credit follows money (cash approve or completed payment). Counter sync and other
              recovery tools are under{" "}
              <Link component={RouterLink} to="/openpaygo-exceptions" onClick={close}>
                OpenPAYGO Exceptions
              </Link>
              {" "}(dual control — not this wizard).
            </Typography>
            {deviceSerial ? (
              <Typography variant="caption" color="text.secondary">
                Device <strong>{deviceSerial}</strong>
                {deviceLoading ? " — loading…" : device ? ` · counter ${device.token_count}` : ""}.
              </Typography>
            ) : null}

            {payment ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Confirm payment</Typography>
                <Typography variant="body2">
                  {payment.method} · {payment.status}
                </Typography>
                {isMobileLike(payment.method) && payment.status === "pending" ? (
                  <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                    Mobile money must finish via provider webhook — not this wizard.
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Next: enter the office / Lipa reference and release the token.
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                {paymentsLoading ? (
                  <Typography variant="body2"><IconSpinner size={14} /> Loading payments…</Typography>
                ) : null}

                <Typography variant="subtitle2">How do you want to release days?</Typography>

                <AppButton
                  type="button"
                  variant={source === "record_cash" ? "primary" : "secondary"}
                  disabled={!device?.customer?.id}
                  title={!device?.customer?.id ? "Assign a customer first" : undefined}
                  onClick={() => {
                    setSource("record_cash");
                    setSelectedPayment(null);
                  }}
                >
                  Record cash collection now (then approve &amp; token)
                </AppButton>

                <AppField label="Or approve pending cash">
                  <select
                    className="pg-select"
                    value={source === "approve_pending" && selectedPayment ? String(selectedPayment.id) : ""}
                    onChange={(e) => {
                      const p = pendingCash.find((x) => String(x.id) === e.target.value);
                      setSelectedPayment(p ?? null);
                      setSource(p ? "approve_pending" : null);
                      if (p) setReference(recordedReceipt(p));
                    }}
                    disabled={pendingCash.length === 0}
                  >
                    <option value="">
                      {pendingCash.length === 0 ? "No pending cash for this customer/device" : "Select pending cash…"}
                    </option>
                    {pendingCash.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        #{p.id} · {p.payment_reference} · {formatTzs(p.amount)}
                        {p.device_serial ? ` · ${p.device_serial}` : ""}
                      </option>
                    ))}
                  </select>
                </AppField>

                <AppField label="Or mint from completed payment">
                  <select
                    className="pg-select"
                    value={source === "mint_completed" && selectedPayment ? String(selectedPayment.id) : ""}
                    onChange={(e) => {
                      const p = completed.find((x) => String(x.id) === e.target.value);
                      setSelectedPayment(p ?? null);
                      setSource(p ? "mint_completed" : null);
                    }}
                    disabled={completed.length === 0}
                  >
                    <option value="">
                      {completed.length === 0 ? "No completed payments yet" : "Select completed payment…"}
                    </option>
                    {completed.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        #{p.id} · {p.payment_reference} · {formatTzs(p.amount)}
                      </option>
                    ))}
                  </select>
                </AppField>

                {pendingMobile.length > 0 ? (
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: "warning.50",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "warning.light",
                    }}
                  >
                    <Typography variant="body2">
                      <strong>{pendingMobile.length}</strong> mobile-money payment(s) waiting for provider
                      confirmation — tokens will issue automatically after the webhook. Do not force-approve here.
                    </Typography>
                  </Box>
                ) : null}

                {!device?.customer?.id ? (
                  <Typography variant="body2" color="warning.main">
                    This device has no customer assigned. Assign a customer first, then return to release days.
                  </Typography>
                ) : null}

                <Typography variant="caption" color="text.secondary">
                  Prefer the field journey?{" "}
                  <Link component={RouterLink} to="/field/payments/new" onClick={close}>
                    Payment Records
                  </Link>
                  {" · "}
                  <Link component={RouterLink} to="/payment-approvals" onClick={close}>
                    Payment Approvals queue
                  </Link>
                  {" · "}
                  <Link component={RouterLink} to="/openpaygo-exceptions" onClick={close}>
                    OpenPAYGO Exceptions
                  </Link>
                </Typography>
              </>
            )}
          </Box>
        )}

        {step === 1 && source === "record_cash" && (
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Typography variant="body2">
              Record cash for <strong>{customerLabel}</strong> on device{" "}
              <strong>{device?.serial_number}</strong>, then approve and issue the token in one step.
            </Typography>
            <AppField label="Amount (TZS)">
              <AppInput
                type="number"
                min={1}
                value={cashAmount}
                onChange={(e) => setCashAmount(e.target.value)}
                placeholder="e.g. 50000"
                disabled={busy}
                autoFocus
              />
            </AppField>
            {cashAmount && Number(cashAmount) > 0 ? (
              <DaysEstimateHint
                amountTzs={Number(cashAmount)}
                customerId={device?.customer?.id ?? selectedPayment?.customer_id}
                compact
              />
            ) : null}
            <AppField label="Approval / receipt reference (min 6 chars)">
              <AppInput
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Office receipt number"
                disabled={busy}
              />
            </AppField>
            <AppField label="Channel">
              <AppSelect value={provider} onChange={(e) => setProvider(e.target.value)} disabled={busy}>
                {PROVIDERS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </AppSelect>
            </AppField>
          </Box>
        )}

        {step === 1 && source === "approve_pending" && selectedPayment && (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Approve cash <strong>{selectedPayment.payment_reference}</strong> ({formatTzs(selectedPayment.amount)})
              and release a token. Enter the <strong>same receipt</strong> that was recorded in the field
              {recordedReceipt(selectedPayment)
                ? ` (${recordedReceipt(selectedPayment)})`
                : ""}.
            </Typography>
            <DaysEstimateHint
              amountTzs={selectedPayment.amount}
              customerId={selectedPayment.customer_id}
            />
            <AppField label="Confirm recorded receipt (must match)">
              <AppInput
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={recordedReceipt(selectedPayment) || "Receipt recorded at create"}
                disabled={busy}
                autoFocus
              />
            </AppField>
            <AppField label="Provider / channel">
              <AppSelect value={provider} onChange={(e) => setProvider(e.target.value)} disabled={busy}>
                {PROVIDERS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </AppSelect>
            </AppField>
          </Box>
        )}

        {step === 1 && source === "mint_completed" && selectedPayment && (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Mint ADD_TIME from completed payment <strong>{selectedPayment.payment_reference}</strong>.
            </Typography>
            <AppField label="Days to add">
              <AppInput
                type="number"
                min={1}
                max={995}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                disabled={busy}
              />
            </AppField>
          </Box>
        )}

        {step === 2 && result && (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              <IconCheck size={16} /> {result.message ?? "Token ready"}
            </Typography>
            {result.token_type ? <AppBadge tone="success">{result.token_type}</AppBadge> : null}
            {result.days_added != null ? (
              <Typography variant="body2" sx={{ mt: 1 }}>Days added: {result.days_added}</Typography>
            ) : null}
            {result.new_token_count != null ? (
              <Typography variant="body2">New server counter: {result.new_token_count}</Typography>
            ) : null}
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "#0f172a",
                color: "#f8fafc",
                borderRadius: 2,
                fontFamily: "ui-monospace, monospace",
                fontSize: 22,
                letterSpacing: 2,
                wordBreak: "break-all",
              }}
            >
              {result.token}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Enter on device: <code>{result.entry_hint}</code>
            </Typography>
            <AppButton
              type="button"
              variant="secondary"
              onClick={() => navigator.clipboard.writeText(result.token)}
              style={{ marginTop: 12 }}
            >
              Copy token
            </AppButton>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3, gap: 1 }}>
          {step === 0 || step === 2 ? (
            <AppButton type="button" variant="secondary" onClick={close} disabled={busy}>
              {step === 2 ? "Close" : "Cancel"}
            </AppButton>
          ) : (
            <AppButton type="button" variant="secondary" onClick={goBack} disabled={busy}>
              Previous
            </AppButton>
          )}
          {step < 2 ? (
            <AppButton type="button" onClick={() => void goNext()} disabled={busy}>
              {busy ? (
                <><IconSpinner size={14} /> Working…</>
              ) : step === 1 ? (
                "Release token"
              ) : (
                "Next"
              )}
            </AppButton>
          ) : (
            <AppButton type="button" onClick={close}>Done</AppButton>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
